import {ChildData, DiaryData, OwnScholarsResponse, UserRolesEnum} from "../../../utilities/constants";
import parentService from "../../../../services/parentService";
import React, {useEffect, useState} from "react";
import {useUser} from "../adminLayout";
import {Accordion} from "react-bootstrap";
import childService from "../../../../services/childService";
import moment from "moment";
import 'moment/locale/it';
import { Gallery } from "react-grid-gallery";
import Lightbox from "react-18-image-lightbox";
import { Image } from "react-grid-gallery";

export interface CustomImage extends Image {
    original: string;
}

function ConsultaDiario() {
    const { user, role, loggedUser } = useUser();
    const [ownChildren, setOwnChildren] = useState<ChildData[]>([]);
    const [childDiary, setChildDiary] = useState<{childId: string, diary: DiaryData[]}[]>([]);

    // Stato per gestire le immagini
    const [diaryImages, setDiaryImages] = useState<{[noteId: string]: any[]}>({});
    const [loadingImages, setLoadingImages] = useState<{[noteId: string]: boolean}>({});
    // Note per cui il recupero immagini è fallito: tenute separate da diaryImages per non
    // confondere "nessuna immagine" con "non sono riuscito a caricarle", così l'utente può riprovare
    const [imageLoadErrors, setImageLoadErrors] = useState<{[noteId: string]: boolean}>({});
    // Tentativi falliti per singola miniatura: 1 = in attesa del retry automatico,
    // 2 = fallito anche il retry, mostriamo un placeholder invece del riquadro vuoto
    const [imageErrors, setImageErrors] = useState<{[imageId: string]: number}>({});
    const [expandedAccordions, setExpandedAccordions] = useState<string[]>([]);

    // Stati per lightbox
    const [lightboxIndex, setLightboxIndex] = useState(-1);
    const [currentGalleryImages, setCurrentGalleryImages] = useState<CustomImage[]>([]);

    // Calcolo immagini per lightbox
    const currentImage = currentGalleryImages[lightboxIndex];
    const nextIndex = (lightboxIndex + 1) % currentGalleryImages.length;
    const nextImage = currentGalleryImages[nextIndex] || currentImage;
    const prevIndex = (lightboxIndex + currentGalleryImages.length - 1) % currentGalleryImages.length;
    const prevImage = currentGalleryImages[prevIndex] || currentImage;

    moment.locale('it');
    useEffect(() => {
        if (role === UserRolesEnum.Parent){
            getOwnChildren();
        }
    }, [role])

    const getOwnChildren = async () => {
        try {
            const response: OwnScholarsResponse = await parentService.getOwnChildren({
                paginate: 25,
                sortBy: 'last_name',
                sort: 'DESC',
                searchQuery: '',
                includeInactive: false
            });
            console.log(response.data);
            setOwnChildren(response.data)
        } catch (error) {
            console.error('Errore durante il recupero dei children del parent:', error);
        }
    }

    useEffect(() => {
        const getDiary = async () => {
            const promises = ownChildren.map(async (child) => {
                try {
                    const response = await childService.getDiary({}, child.id);
                    return { childId: child.id, diary: response.data };
                } catch (error) {
                    console.error('Errore durante il recupero del diario del bambino:', error);
                    return null;
                }
            });

            const diaries = await Promise.all(promises);
            const filteredDiaries = diaries.filter(diary => diary !== null) as { childId: string; diary: DiaryData[]; }[];
            setChildDiary(filteredDiaries);
            console.log(childDiary);
        };

        if (ownChildren.length > 0) {
            getDiary();
        }
    }, [ownChildren]);

    // Funzione per caricare le immagini di una nota specifica
    const loadDiaryImages = async (noteId: string) => {
        // Se le immagini sono già in cache, non ricaricare
        if (diaryImages[noteId]) {
            return;
        }

        // Se sta già caricando, non fare nulla
        if (loadingImages[noteId]) {
            return;
        }

        setLoadingImages(prev => ({ ...prev, [noteId]: true }));
        setImageLoadErrors(prev => ({ ...prev, [noteId]: false }));

        try {
            const images = await childService.getImages(noteId);
            setDiaryImages(prev => ({ ...prev, [noteId]: images || [] }));
        } catch (error) {
            console.error('Errore durante il caricamento delle immagini:', error);
            // Non salviamo un array vuoto in cache: se le immagini esistono ma la richiesta è
            // fallita (es. errore momentaneo del backend), vogliamo poter riprovare più tardi
            setImageLoadErrors(prev => ({ ...prev, [noteId]: true }));
        } finally {
            setLoadingImages(prev => ({ ...prev, [noteId]: false }));
        }
    };

    // Ritenta una volta il caricamento di una miniatura dopo un breve errore (es. backend momentaneamente
    // sotto carico); se fallisce anche il retry mostriamo un placeholder invece del riquadro vuoto
    const handleImageError = (imageId: string) => {
        setImageErrors(prev => {
            const attempts = prev[imageId] || 0;
            if (attempts === 0) {
                setTimeout(() => setImageErrors(p => ({ ...p, [imageId]: 1 })), 2000);
                return prev;
            }
            return { ...prev, [imageId]: 2 };
        });
    };

    // Permette di ritentare manualmente una miniatura dopo che anche il retry automatico è fallito
    const retryImage = (imageId: string) => {
        setImageErrors(prev => ({ ...prev, [imageId]: 0 }));
    };

    // Gestisce l'apertura/chiusura degli accordion
    const handleAccordionToggle = (eventKey: string, diaryEntry: DiaryData) => {
        const isExpanding = !expandedAccordions.includes(eventKey);

        if (isExpanding) {
            setExpandedAccordions(prev => [...prev, eventKey]);
            // Carica le immagini solo se ci sono e se non sono già state caricate
            if (diaryEntry.images_count > 0) {
                loadDiaryImages(diaryEntry.id);
            }
        } else {
            setExpandedAccordions(prev => prev.filter(key => key !== eventKey));
        }
    };

    // Funzioni per gestire la lightbox
    const handleImageClick = (imageIndex: number, noteId: string) => {
        const images = diaryImages[noteId] || [];
        const imagesToShow: CustomImage[] = images.map((image, idx) => ({
            src: image.download_url,
            original: image.download_url,
            caption: `Immagine ${idx + 1} - ${moment(diaryImages[noteId]?.[0]?.created_at).format('DD MMMM YYYY')}`,
            width: 100,
            height: 100
        }));

        setCurrentGalleryImages(imagesToShow);
        setLightboxIndex(imageIndex);
    };

    const handleCloseLightbox = () => setLightboxIndex(-1);
    const handleMovePrev = () => setLightboxIndex(prevIndex);
    const handleMoveNext = () => setLightboxIndex(nextIndex);

    // Funzione di utilità per mappare i valori del cibo
    const mapFoodValue = (value: number): string => {
        switch (value) {
            case 0:
                return "Niente";
            case 1:
                return "Poco";
            case 2:
                return "Metà";
            case 3:
                return "Tutto";
            case 4:
                return "Bis";
            default:
                return "";
        }
    };
    // Funzione di utilità per mappare la qualità delle feci
    const mapFecesQuality = (value: number): string => {
        switch (value) {
            case 0:
                return "Liquida";
            case 1:
                return "Molle";
            case 2:
                return "Bella";
            default:
                return "";
        }
    };

    // Funzione di utilità per mappare la quantità di latte
    const mapMilkQuantity = (value: number): string => {
        switch (value) {
            case 0:
                return "Niente";
            case 1:
                return "Poco";
            case 2:
                return "Metà";
            case 3:
                return "Tutto";
            case 4:
                return "Bis";
            default:
                return "";
        }
    };//nap_1_quality

    // Funzione di utilità per mappare la quantità di sonno
    const mapNapQuality = (value: number): string => {
        switch (value) {
            case 0:
                return "Mezz'ora";
            case 1:
                return "1 ora";
            case 2:
                return "1 ora e mezza";
            case 3:
                return "2 ore";
            case 4:
                return "2 ore e mezza";
            case 5:
                return "3 ore";
            default:
                return "";
        }
    };

    return(
        <>
            <Accordion>
                {ownChildren.map((child) => (
                    <Accordion.Item key={child.id} eventKey={child.id}>
                        <Accordion.Header>{child.first_name}</Accordion.Header>
                        <Accordion.Body>
                            {childDiary
                                .filter((entry) => entry.childId === child.id)
                                .map((entry, index) => (
                                    <React.Fragment key={index}>
                                        {entry.diary.map((diaryEntry, diaryIndex) => {
                                            const accordionKey = `${child.id}-${diaryIndex}`;
                                            const isExpanded = expandedAccordions.includes(accordionKey);
                                            return (
                                                <Accordion key={diaryIndex}>
                                                    <Accordion.Item eventKey={accordionKey}>
                                                        <Accordion.Header
                                                            onClick={() => handleAccordionToggle(accordionKey, diaryEntry)}
                                                        >
                                                            <div className="d-flex align-items-center justify-content-between w-100 me-3">
                                                                <span>
                                                                  <b className="tertiary">{moment(diaryEntry.created_at).format('DD MMMM YYYY')}</b>
                                                                </span>
                                                                {diaryEntry.images_count > 0 && (
                                                                  <span className="badge bg-primary">
                                                                    📸 {diaryEntry.images_count}
                                                                  </span>
                                                                )}
                                                            </div>
                                                        </Accordion.Header>
                                                        <Accordion.Body>
                                                            <p><b>Primo:</b> {mapFoodValue(diaryEntry.food_first_course)}</p>
                                                            <p><b>Secondo:</b> {mapFoodValue(diaryEntry.food_second_course)}</p>
                                                            <p><b>Contorno:</b> {mapFoodValue(diaryEntry.food_side)}</p>
                                                            <p><b>Latte:</b> {mapMilkQuantity(diaryEntry.milk)}</p>
                                                            <p><b>Cacca 1:</b> {mapFecesQuality(diaryEntry.feces_1_quality)}</p>
                                                            <p><b>Cacca 2:</b> {mapFecesQuality(diaryEntry.feces_2_quality)}</p>
                                                            <p><b>Nanna mattino:</b> {mapNapQuality(diaryEntry.nap_1_quality)}</p>
                                                            <p><b>Nanna pomeriggio:</b> {mapNapQuality(diaryEntry.nap_2_quality)}</p>
                                                            <p><b>Attività:</b> {diaryEntry.activities}</p>
                                                            <p><b>Note:</b> {diaryEntry.notes}</p>
                                                            {/* Sezione immagini */}
                                                            {diaryEntry.images_count > 0 && (
                                                                <div className="mt-3">
                                                                    <h6><b>Immagini:</b></h6>
                                                                    {loadingImages[diaryEntry.id] ? (
                                                                        <div className="d-flex justify-content-center py-3">
                                                                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                                                <span className="visually-hidden">Caricamento immagini...</span>
                                                                            </div>
                                                                        </div>
                                                                    ) : imageLoadErrors[diaryEntry.id] ? (
                                                                        <div className="text-center py-2">
                                                                            <small className="text-danger d-block mb-1">Impossibile caricare le immagini.</small>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-outline-primary"
                                                                                onClick={() => loadDiaryImages(diaryEntry.id)}
                                                                            >
                                                                                Riprova
                                                                            </button>
                                                                        </div>
                                                                    ) : diaryImages[diaryEntry.id] ? (
                                                                        <div className="row g-2">
                                                                            {diaryImages[diaryEntry.id].map((image, imgIndex) => (
                                                                                <div key={imgIndex} className="col-md-4">
                                                                                    {imageErrors[image.id] === 2 ? (
                                                                                        <div
                                                                                            className="d-flex flex-column align-items-center justify-content-center bg-light rounded text-muted"
                                                                                            style={{ width: '100%', height: '150px', cursor: 'pointer' }}
                                                                                            title="Immagine non disponibile, clicca per vederla in grande o riprova"
                                                                                            onClick={() => handleImageClick(imgIndex, diaryEntry.id)}
                                                                                        >
                                                                                            <small className="mb-1">Immagine non disponibile</small>
                                                                                            <button
                                                                                                type="button"
                                                                                                className="btn btn-sm btn-outline-primary"
                                                                                                onClick={(e) => { e.stopPropagation(); retryImage(image.id); }}
                                                                                            >
                                                                                                Riprova
                                                                                            </button>
                                                                                        </div>
                                                                                    ) : (
                                                                                        <img
                                                                                            src={imageErrors[image.id] === 1 ? `${image.download_url}?retry=1` : image.download_url}
                                                                                            alt={`Immagine ${imgIndex + 1}`}
                                                                                            className="img-fluid rounded"
                                                                                            style={{
                                                                                                width: '100%',
                                                                                                height: '150px',
                                                                                                objectFit: 'cover',
                                                                                                cursor: 'pointer'
                                                                                            }}
                                                                                            onClick={() => handleImageClick(imgIndex, diaryEntry.id)}
                                                                                            onError={() => handleImageError(image.id)}
                                                                                        />
                                                                                    )}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    ) : null}
                                                                </div>
                                                            )}
                                                        </Accordion.Body>
                                                    </Accordion.Item>
                                                </Accordion>
                                            );
                                        })}
                                    </React.Fragment>
                                ))}
                        </Accordion.Body>
                    </Accordion.Item>
                ))}
            </Accordion>
            {/* Lightbox per visualizzare le immagini */}
            {lightboxIndex >= 0 && !!currentImage && (
                /* @ts-ignore */
                // Con una sola immagine, next/prev coinciderebbero con quella corrente:
                // li omettiamo per evitare richieste duplicate della stessa immagine
                <Lightbox
                    mainSrc={currentImage.original}
                    imageTitle={currentImage.caption}
                    mainSrcThumbnail={currentImage.src}
                    nextSrc={currentGalleryImages.length > 1 ? nextImage?.original : undefined}
                    nextSrcThumbnail={currentGalleryImages.length > 1 ? nextImage?.src : undefined}
                    prevSrc={currentGalleryImages.length > 1 ? prevImage?.original : undefined}
                    prevSrcThumbnail={currentGalleryImages.length > 1 ? prevImage?.src : undefined}
                    onCloseRequest={handleCloseLightbox}
                    onMovePrevRequest={handleMovePrev}
                    onMoveNextRequest={handleMoveNext}
                />
            )}
        </>
    )
}
export default ConsultaDiario;
